import { useState, useEffect } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Hash,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  RefreshCw,
} from 'lucide-react'
import {
  useMySecuritiesAccount,
  useCreateOrder,
  useMyOrders,
  useDeleteOrder,
} from '@/hooks/useOrders'
import { usePriceSpreads } from '@/hooks/useSecurity'
import { formatCurrency, formatNumber, cn } from '@/lib/utils'
import type { OrderAction, OrderType, SecurityOrder, OrderValidationResponse } from '@/types/order'
import type { PriceSpread } from '@/types/security'

/**
 * Trading Page - Create buy/sell orders for securities.
 *
 * Features:
 * - Security search and selection
 * - Buy/Sell order creation
 * - Market and Limit order types
 * - Real-time price display
 * - Order validation before submission
 * - Pending orders list
 */

export function TradingPage() {
  const { data: account, isLoading: accountLoading } = useMySecuritiesAccount()

  if (accountLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-text-light">Loading trading interface...</p>
        </div>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
          <p className="text-lg font-semibold text-text-dark">No Securities Account</p>
          <p className="text-text-muted">You need a securities account to trade.</p>
        </div>
      </div>
    )
  }

  return <TradingInterface accountId={account.id} />
}

interface TradingInterfaceProps {
  accountId: string
}

function TradingInterface({ accountId }: TradingInterfaceProps) {
  // Form state
  const [securitySearch, setSecuritySearch] = useState('')
  const [selectedSecurity, setSelectedSecurity] = useState<PriceSpread | null>(null)
  const [action, setAction] = useState<OrderAction>('BUY')
  const [orderType, setOrderType] = useState<OrderType>('LIMIT')
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')

  // Validation state
  const [validationResult, setValidationResult] = useState<OrderValidationResponse | null>(null)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [orderSuccess, setOrderSuccess] = useState(false)

  // Search results
  const { data: searchResults } = usePriceSpreads({
    search: securitySearch.length >= 2 ? securitySearch : undefined,
    size: 10,
  })

  // Pending orders
  const { data: ordersData, refetch: refetchOrders } = useMyOrders(accountId)
  const pendingOrders = ordersData?.content || []

  // Mutations
  const createOrder = useCreateOrder()
  const deleteOrder = useDeleteOrder()

  // Update price when selecting a security
  useEffect(() => {
    if (selectedSecurity) {
      const suggestedPrice =
        action === 'BUY' ? selectedSecurity.askPrice : selectedSecurity.bidPrice
      if (suggestedPrice) {
        setPrice(suggestedPrice.toFixed(2))
      }
    }
  }, [selectedSecurity, action])

  // Validate order when inputs change
  useEffect(() => {
    if (!selectedSecurity || !quantity || (orderType === 'LIMIT' && !price)) {
      setValidationResult(null)
      return
    }

    const timer = setTimeout(() => {
      createOrder.mutate(
        {
          owner: accountId,
          securityIdentifier: selectedSecurity.securityIdentifier,
          action,
          type: orderType,
          numberOfShares: parseInt(quantity, 10),
          price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
          checkOrderOnly: true,
        },
        {
          onSuccess: (data) => {
            setValidationResult(data as OrderValidationResponse)
            setOrderError(null)
          },
          onError: (error) => {
            setOrderError(error.message)
            setValidationResult(null)
          },
        }
      )
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createOrder.mutate is stable (React Query)
  }, [selectedSecurity, action, orderType, quantity, price, accountId])

  // Handle order submission
  const handleSubmitOrder = () => {
    if (!selectedSecurity || !quantity) return

    setOrderError(null)
    setOrderSuccess(false)

    createOrder.mutate(
      {
        owner: accountId,
        securityIdentifier: selectedSecurity.securityIdentifier,
        action,
        type: orderType,
        numberOfShares: parseInt(quantity, 10),
        price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
        checkOrderOnly: false,
      },
      {
        onSuccess: () => {
          setOrderSuccess(true)
          setQuantity('')
          setPrice('')
          setSelectedSecurity(null)
          setSecuritySearch('')
          refetchOrders()
          setTimeout(() => setOrderSuccess(false), 3000)
        },
        onError: (error) => {
          setOrderError(error.message)
        },
      }
    )
  }

  // Handle order cancellation
  const handleCancelOrder = (orderId: string) => {
    deleteOrder.mutate(orderId, {
      onSuccess: () => refetchOrders(),
    })
  }

  const isValid = validationResult && !validationResult.checkResult.failed
  const canSubmit = isValid && !createOrder.isPending

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <h1 className="text-2xl font-semibold text-text-dark">Trading</h1>
        <p className="text-sm text-text-muted">Create buy and sell orders for securities</p>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Order Form */}
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h2 className="mb-4 text-lg font-semibold text-text-dark">New Order</h2>

              {/* Security Search */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-text-dark">Security</label>
                {selectedSecurity ? (
                  <div className="flex items-center justify-between rounded-md border border-primary bg-primary-50 p-3">
                    <div>
                      <p className="font-medium text-text-dark">{selectedSecurity.name}</p>
                      <p className="text-xs text-text-muted">
                        {selectedSecurity.securityIdentifier}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedSecurity(null)
                        setSecuritySearch('')
                      }}
                      className="rounded p-1 hover:bg-primary-100"
                    >
                      <X className="h-4 w-4 text-text-muted" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by name or identifier..."
                      value={securitySearch}
                      onChange={(e) => setSecuritySearch(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {securitySearch.length >= 2 && searchResults?.content && (
                      <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                        {searchResults.content.length === 0 ? (
                          <p className="p-3 text-sm text-text-muted">No securities found</p>
                        ) : (
                          searchResults.content.map((security) => (
                            <button
                              key={security.id}
                              onClick={() => {
                                setSelectedSecurity(security)
                                setSecuritySearch('')
                              }}
                              className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                            >
                              <div>
                                <p className="text-sm font-medium text-text-dark">
                                  {security.name}
                                </p>
                                <p className="text-xs text-text-muted">
                                  {security.securityIdentifier}
                                </p>
                              </div>
                              {security.lastPrice && (
                                <span className="text-sm font-medium">
                                  {formatCurrency(security.lastPrice)}
                                </span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Price Info */}
              {selectedSecurity && (
                <div className="mb-4 grid grid-cols-3 gap-2 rounded-md bg-gray-50 p-3 text-sm">
                  <div>
                    <p className="text-text-muted">Bid</p>
                    <p className="font-medium text-success">
                      {selectedSecurity.bidPrice ? formatCurrency(selectedSecurity.bidPrice) : '-'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-text-muted">Last</p>
                    <p className="font-medium">
                      {selectedSecurity.lastPrice
                        ? formatCurrency(selectedSecurity.lastPrice)
                        : '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-text-muted">Ask</p>
                    <p className="font-medium text-red-500">
                      {selectedSecurity.askPrice ? formatCurrency(selectedSecurity.askPrice) : '-'}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Toggle */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-text-dark">Action</label>
                <div className="flex rounded-md border border-gray-300">
                  <button
                    onClick={() => setAction('BUY')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-l-md px-4 py-2 text-sm font-medium transition-colors',
                      action === 'BUY'
                        ? 'bg-success text-white'
                        : 'bg-white text-text-dark hover:bg-gray-50'
                    )}
                  >
                    <TrendingUp className="h-4 w-4" />
                    Buy
                  </button>
                  <button
                    onClick={() => setAction('SELL')}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-r-md px-4 py-2 text-sm font-medium transition-colors',
                      action === 'SELL'
                        ? 'bg-red-500 text-white'
                        : 'bg-white text-text-dark hover:bg-gray-50'
                    )}
                  >
                    <TrendingDown className="h-4 w-4" />
                    Sell
                  </button>
                </div>
              </div>

              {/* Order Type Toggle */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-text-dark">Order Type</label>
                <div className="flex rounded-md border border-gray-300">
                  <button
                    onClick={() => setOrderType('LIMIT')}
                    className={cn(
                      'flex-1 rounded-l-md px-4 py-2 text-sm font-medium transition-colors',
                      orderType === 'LIMIT'
                        ? 'bg-primary text-white'
                        : 'bg-white text-text-dark hover:bg-gray-50'
                    )}
                  >
                    Limit
                  </button>
                  <button
                    onClick={() => setOrderType('MARKET')}
                    className={cn(
                      'flex-1 rounded-r-md px-4 py-2 text-sm font-medium transition-colors',
                      orderType === 'MARKET'
                        ? 'bg-primary text-white'
                        : 'bg-white text-text-dark hover:bg-gray-50'
                    )}
                  >
                    Market
                  </button>
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-text-dark">Quantity</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Number of shares"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Price (for limit orders) */}
              {orderType === 'LIMIT' && (
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-text-dark">
                    Limit Price
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Price per share"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
              )}

              {/* Estimated Volume */}
              {validationResult?.executionVolume && (
                <div className="mb-4 rounded-md bg-gray-50 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Estimated Total</span>
                    <span className="font-semibold text-text-dark">
                      {formatCurrency(validationResult.executionVolume)}
                    </span>
                  </div>
                  {validationResult.executionPrice && (
                    <div className="mt-1 flex items-center justify-between text-sm">
                      <span className="text-text-muted">Execution Price</span>
                      <span className="text-text-dark">
                        {formatCurrency(validationResult.executionPrice)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Status */}
              {validationResult && (
                <div
                  className={cn(
                    'mb-4 flex items-center gap-2 rounded-md p-3 text-sm',
                    validationResult.checkResult.failed
                      ? 'bg-red-50 text-red-700'
                      : 'bg-green-50 text-green-700'
                  )}
                >
                  {validationResult.checkResult.failed ? (
                    <>
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {validationResult.checkResult.msg?.message || 'Order validation failed'}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Order is valid</span>
                    </>
                  )}
                </div>
              )}

              {/* Error Message */}
              {orderError && (
                <div className="mb-4 flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <span>{orderError}</span>
                </div>
              )}

              {/* Success Message */}
              {orderSuccess && (
                <div className="mb-4 flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  <span>Order placed successfully!</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmitOrder}
                disabled={!canSubmit}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-medium transition-colors',
                  action === 'BUY'
                    ? 'bg-success text-white hover:bg-success-dark disabled:bg-gray-300'
                    : 'bg-red-500 text-white hover:bg-red-600 disabled:bg-gray-300',
                  'disabled:cursor-not-allowed'
                )}
              >
                {createOrder.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {action === 'BUY' ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    Place {action} Order
                  </>
                )}
              </button>
            </div>

            {/* Pending Orders */}
            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-dark">Pending Orders</h2>
                <button
                  onClick={() => refetchOrders()}
                  className="rounded p-1 hover:bg-gray-100"
                  title="Refresh orders"
                >
                  <RefreshCw className="h-4 w-4 text-text-muted" />
                </button>
              </div>

              {pendingOrders.length === 0 ? (
                <div className="py-8 text-center text-text-muted">
                  <p>No pending orders</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingOrders.map((order) => (
                    <OrderCard key={order.id} order={order} onCancel={handleCancelOrder} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Order card component
interface OrderCardProps {
  order: SecurityOrder
  onCancel: (id: string) => void
}

function OrderCard({ order, onCancel }: OrderCardProps) {
  const isBuy = order.action === 'BUY'

  return (
    <div className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 p-3">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            isBuy ? 'bg-success-100 text-success' : 'bg-red-100 text-red-500'
          )}
        >
          {isBuy ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
        </div>
        <div>
          <p className="text-sm font-medium text-text-dark">
            {order.action} {formatNumber(order.numberOfShares)} {order.securityIdentifier}
          </p>
          <p className="text-xs text-text-muted">
            {order.type} {order.price ? `@ ${formatCurrency(order.price)}` : ''}
          </p>
        </div>
      </div>
      <button
        onClick={() => onCancel(order.id)}
        className="rounded p-1 text-text-muted hover:bg-gray-200 hover:text-red-500"
        title="Cancel order"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
